<script lang="ts" setup>
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { ShowcaseLayout } from '@Shared';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import useFlowNavigation from '@Use/useFlowNavigation';
import UpcomingStepTile from '@Components/shared/UpcomingStepTile.vue';
import ShowcaseImage from '@Components/ShowcaseImage.vue';
import { useRouter } from 'vue-router';

const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const router = useRouter();

// Static steps for the "What Happens Next" screen
const upcomingSteps = [
    {
        id: 1,
        displayName: 'Verify your identity',
        icon: 'cd-ic-scan'
    },
    {
        id: 2,
        displayName: 'Review your document',
        icon: 'cd-ic-document'
    },
    {
        id: 3,
        displayName: 'Complete document tasks',
        icon: 'cd-ic-check-circle'
    },
    {
        id: 4,
        displayName: 'Send finished document',
        icon: 'cd-ic-paper-plane'
    }
];


// Actions
const continueFlow = () => {
    router.push({ name: 'startup' });
};

</script>


<template>
    <showcase-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        show-back-button
        @back-click="goBackInFlow"
    >

        <div class="content-wrapper">
            <h1>What happens next</h1>
            <p class="subheading">Here's what to expect during the next few minutes.</p>

            <div class="tiles-and-button-wrapper">
                <div class="steps-container">
                    <div
                        class="upcoming-steps"
                        v-for="step in upcomingSteps"
                        :key="step.id"
                    >
                        <upcoming-step-tile
                            :title="step.displayName"
                            :icon="step.icon"
                        />
                    </div>
                </div>

                <app-button
                    expanded
                    size="large"
                    @on-click="continueFlow"
                    class="continue-btn"
                    data-id="whats-next-continue"
                >
                    Continue
                </app-button>
            </div>
        </div>

        <template #branded>
            <showcase-image />
        </template>
    </showcase-layout>
</template>


<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

// Override back button position to align arrow icon with h1 text
// Accounting for the button's internal padding
:deep(.back-button) {
    @include tablet-and-down {
        left: 4rem !important;
    }
}

.content-wrapper {
    display: flex;
    flex-direction: column;
    padding: 0 2rem;

    @include desktop {
        padding: 0;
        height: 100%;
    }
}

h1 {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.2;
    color: var(--ch-primary-dark);
    margin-bottom: 1rem;
    margin-top: 0;
    white-space: nowrap;

    @include desktop {
        font-size: 2.5rem;
    }
}

.subheading {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    margin-bottom: 2rem;
}

.tiles-and-button-wrapper {
    width: 100%;

    @include desktop {
        max-width: 20rem;
        margin: 0 auto;
        flex: 1;
        display: flex;
        flex-direction: column;
    }
}

.steps-container {
    margin-bottom: 1rem;

    @include desktop {
        flex: 1;
        margin-bottom: 2rem;
    }
}

.upcoming-steps {
    margin-bottom: 1rem;

    // Override tile width to match button width on mobile
    @include tablet-and-down {
        :deep(.upcoming-step-tile) {
            margin-left: 0;
            margin-right: 0;
        }
    }
}

.continue-btn {
    margin-top: 0.5rem;
    margin-bottom: 1.5rem;

    @include desktop {
        margin-top: 0;
        margin-bottom: 2rem;
    }
}
</style>